import { Navigate, createBrowserRouter, redirect } from "react-router-dom"

import CommonLayout from "./common/components/CommonLayout"
import AdminLayout from "./pages/admin"
import AdminActivityReportEmails from "./pages/admin/activity-report-emails"
import AdminAssignments from "./pages/admin/assignments"
import AdminAuditLogs from "./pages/admin/audit-logs"
import AdminAuthLayout from "./pages/admin/authLayout"
import AdminConciergeCallingNumbers from "./pages/admin/concierge-calling-numbers"
import AdminConciergeDetail from "./pages/admin/concierge-detail"
import AdminConcierges from "./pages/admin/concierges"
import AdminDashboard from "./pages/admin/dashboard"
import AdminForgotPassword from "./pages/admin/forgot-password"
import AdminForgotPasswordEmailSent from "./pages/admin/forgot-password-email-sent"
import AdminInboundVoicemail from "./pages/admin/inbound-voicemail"
import AdminInboxSmsTemplates from "./pages/admin/inbox-sms-templates/index"
import AdminInboxTemplates from "./pages/admin/inbox-templates/index"
import AdminLogIn from "./pages/admin/log-in"
import AdminMeetingConfiguration from "./pages/admin/meetings/MeetingConfigurationPage"
import AdminMeetingsAnalytics from "./pages/admin/meetings/MeetingsAnalyticsPage"
import AdminMeetingsCalendar from "./pages/admin/meetings/MeetingsCalendarPage"
import AdminMeetingsList from "./pages/admin/meetings/MeetingsListPage"
import AdminMlsPurchases from "./pages/admin/mls-purchases"
import AdminPods from "./pages/admin/pods"
import AdminPodDetail from "./pages/admin/pods/PodDetail"
import PodDialerGate from "./pages/admin/pods/PodDialerGate"
import AdminSystemStatus from "./pages/admin/system-status"
import AdminTrialManagement from "./pages/admin/trial-management"
import AdminUsers from "./pages/admin/users"
import AdminZohoMailOrg from "./pages/admin/zoho-mail-org"
import AdminZohoMailbox from "./pages/admin/zoho-mailbox"
import AdminZohoMailboxAudit from "./pages/admin/zoho-mailbox-audit"
import PublicBookingPage from "./pages/book-meeting"
import BuyMlsPage from "./pages/buy-mls"
import PageNotFound from "./pages/common/404"
import AiUsagePage from "./pages/common/ai-usage"
import PrivacyPolicy from "./pages/common/privacy-policy"
import TermsAndConditions from "./pages/common/terms-conditions"
import CustomerLayout from "./pages/customer"
import CustomerAccessForm from "./pages/customer-access-form"
import Activity from "./pages/customer/activity"
import AirtableMagicLogin from "./pages/customer/airtable-magic-login"
import CandidateDetails from "./pages/customer/candidate-details"
import Candidates from "./pages/customer/candidates"
import ConciergePage from "./pages/customer/concierge"
import EmailCampaignList from "./pages/customer/email-campaigns/list"
import Messages from "./pages/customer/messages"
import Leads from "./pages/customer/my-mls"
import LeadDetailsPage from "./pages/customer/my-mls/LeadDetailsPage"
import MyStrategyCenter from "./pages/customer/my-strategy-center"
import Pipeline from "./pages/customer/pipeline"
import OfferList from "./pages/customer/proposals"
import {
  CUSTOMER_REDIRECT_ROUTES,
  TEMPLATES_INDEX_ROUTE,
} from "./pages/customer/redirects"
import Settings from "./pages/customer/settings"
import SMSCampaignList from "./pages/customer/sms-campaigns/list"
import Tasks from "./pages/customer/tasks"
import WorkflowEditor from "./pages/customer/workflows/editor"
import Workflows from "./pages/customer/workflows/list"
import PayMlsPage from "./pages/pay-mls"
import TextsPage from "./pages/texts"
import UsersLayout from "./pages/users"
import AcceptInvite from "./pages/users/accept-invite"
import EmailConfirmationInvalid from "./pages/users/email-confirmation-invalid"
import EmailConfirmed from "./pages/users/email-confirmed"
import ForgotPassword from "./pages/users/forgot-password"
import ForgotPasswordEmailSent from "./pages/users/forgot-password-email-sent"
import LogIn from "./pages/users/log-in"
import ResetPassword from "./pages/users/reset-password"
import SignUp from "./pages/users/sign-up"
import SignupSuccessful from "./pages/users/signup-successful"

export default createBrowserRouter([
  {
    path: "/",
    element: <CommonLayout />,
    children: [
      {
        path: "privacy-policy/",
        element: <PrivacyPolicy />,
      },
      {
        path: "terms/",
        element: <TermsAndConditions />,
      },
      {
        path: "ai-usage/",
        element: <AiUsagePage />,
      },
      {
        path: "/user",
        element: <UsersLayout />,
        children: [
          {
            path: "log-in/",
            element: <LogIn />,
          },
          {
            path: "sign-up/",
            element: <SignUp />,
          },
          {
            path: "signup-success/",
            element: <SignupSuccessful />,
          },
          {
            path: "email-confirmation-invalid/",
            element: <EmailConfirmationInvalid />,
          },
          {
            path: "email-confirmed/",
            element: <EmailConfirmed />,
          },
          {
            path: "forgot-password/",
            element: <ForgotPassword />,
          },
          {
            path: "forgot-password-email-sent/",
            element: <ForgotPasswordEmailSent />,
          },
          {
            path: "reset-password/",
            element: <ResetPassword />,
          },
          {
            path: "accept-invite/",
            element: <AcceptInvite />,
          },
        ],
      },
      {
        path: "/login",
        loader: () => {
          return redirect("/user/log-in/")
        },
      },
      {
        path: "/email-confirmation-invalid",
        element: <Navigate to="/user/email-confirmation-invalid" />,
      },
      {
        path: "/signup-confirmed",
        element: <Navigate to="/user/email-confirmed" />,
      },
      {
        path: "/users/reset-password",
        loader: ({ request }) => {
          const searchParams = new URL(request.url).searchParams
          return redirect(`/user/reset-password?${searchParams.toString()}`)
        },
      },
      {
        path: "/users/magic-login",
        element: <AirtableMagicLogin />,
      },
      {
        path: "/texts",
        element: <TextsPage />,
      },
      {
        // Standalone: an MLS purchase link, never a login into the app.
        path: "/buy-mls/:signature",
        element: <BuyMlsPage />,
      },
      {
        // Standalone: pay an MLS order someone else filled in; no login.
        path: "/pay-mls/:signature",
        element: <PayMlsPage />,
      },
      {
        // Standalone: book meetings for one user from a link staff shared; no login.
        path: "/book/:token",
        element: <PublicBookingPage />,
      },
      {
        path: "/customer-access-form",
        element: <AdminAuthLayout />,
        children: [{ index: true, element: <CustomerAccessForm /> }],
      },
      {
        path: "/users/accept-invite",
        loader: ({ request }) => {
          const searchParams = new URL(request.url).searchParams
          return redirect(`/user/accept-invite/?${searchParams.toString()}`)
        },
      },
      {
        path: "/admin",
        children: [
          {
            element: <AdminAuthLayout />,
            children: [
              {
                path: "log-in/",
                element: <AdminLogIn />,
              },
              {
                path: "forgot-password/",
                element: <AdminForgotPassword />,
              },
              {
                path: "forgot-password-email-sent/",
                element: <AdminForgotPasswordEmailSent />,
              },
            ],
          },
          {
            element: <AdminLayout />,
            children: [
              {
                index: true,
                element: <AdminDashboard />,
              },
              {
                path: "users/",
                element: <AdminUsers />,
              },
              {
                path: "trial-management/",
                element: <AdminTrialManagement />,
              },
              {
                path: "system-status/",
                element: <AdminSystemStatus />,
              },
              {
                path: "mls-purchases/",
                element: <AdminMlsPurchases />,
              },
              {
                path: "assignments/",
                element: <AdminAssignments />,
              },
              {
                path: "pods/",
                element: (
                  <PodDialerGate>
                    <AdminPods />
                  </PodDialerGate>
                ),
              },
              {
                path: "pods/:podId",
                element: (
                  <PodDialerGate>
                    <AdminPodDetail />
                  </PodDialerGate>
                ),
              },
              {
                path: "concierges/",
                element: <AdminConcierges />,
              },
              {
                path: "concierges/:conciergeId",
                element: <AdminConciergeDetail />,
              },
              {
                path: "concierge-calling-numbers/",
                element: <AdminConciergeCallingNumbers />,
              },
              {
                path: "inbound-voicemail/",
                element: <AdminInboundVoicemail />,
              },
              {
                path: "zoho-mail-org/",
                element: <AdminZohoMailOrg />,
              },
              {
                path: "zoho-mailbox-audit/",
                element: <AdminZohoMailboxAudit />,
              },
              {
                path: "zoho-mailbox/",
                element: <AdminZohoMailbox />,
              },
              {
                path: "inbox-templates/",
                element: <AdminInboxTemplates />,
              },
              {
                path: "inbox-sms-templates/",
                element: <AdminInboxSmsTemplates />,
              },
              {
                path: "audit-logs/",
                element: <AdminAuditLogs />,
              },
              {
                path: "meetings/",
                element: <AdminMeetingsCalendar />,
              },
              {
                path: "meetings/list/",
                element: <AdminMeetingsList />,
              },
              {
                path: "meetings/analytics/",
                element: <AdminMeetingsAnalytics />,
              },
              {
                path: "meetings/configuration/",
                element: <AdminMeetingConfiguration />,
              },
              {
                path: "activity-report-emails/",
                element: <AdminActivityReportEmails />,
              },
            ],
          },
        ],
      },
      {
        path: "/",
        element: <CustomerLayout />,
        children: [
          ...CUSTOMER_REDIRECT_ROUTES,
          {
            path: "candidates/",
            element: <Candidates />,
          },
          {
            path: "candidates/:id",
            element: <CandidateDetails />,
          },
          {
            path: "activity/",
            element: <Activity />,
          },
          {
            path: "strategy-center/resources/",
            element: <MyStrategyCenter />,
          },
          {
            path: "pipeline/",
            element: <Pipeline />,
          },
          {
            path: "messages/",
            element: <Messages />,
          },
          {
            path: "settings/",
            element: <Settings />,
          },
          {
            path: "concierge/",
            element: <ConciergePage />,
          },
          {
            path: "proposals/",
            element: <OfferList />,
          },
          {
            path: "templates/",
            children: [
              TEMPLATES_INDEX_ROUTE,
              {
                path: "sms/",
                element: <SMSCampaignList />,
              },
              {
                path: "email/",
                element: <EmailCampaignList />,
              },
            ],
          },
          {
            path: "tasks/",
            element: <Tasks />,
          },
          {
            path: "workflows/",
            element: <Workflows />,
          },
          {
            path: "workflows/:id/",
            element: <WorkflowEditor />,
          },
          {
            path: "leads/",
            element: <Leads />,
          },
          {
            path: "leads/:leadSource/:id",
            element: <LeadDetailsPage />,
          },
        ],
      },
      {
        path: "/*",
        element: <PageNotFound />,
      },
    ],
  },
])
